/**
 * @trovex/auth core — framework-agnostic Trovex ID auth engine.
 *
 * Generalised from apps/voip/src/hooks/useAuth.js (the gold-standard
 * implementation) so every app stops carrying its own copy. Two layers:
 *   - this core: no React, just the Firebase SDK + localStorage. All the
 *     hard-won behaviour lives here (SSO token exchange, claim-read retry
 *     with per-uid email cache, stale-while-revalidate platform-access
 *     gate, persistence rollback hatch, transient-session handling flags).
 *   - modules/auth/react: a hook + Login component wrapping this core.
 *
 * users.trovex.com is the first consumer (phase-2 dogfood); existing apps
 * migrate one PR at a time.
 *
 * Usage:
 *   const core = createAuthCore({ auth, db, functions, appId: 'users',
 *                                 platformId: 'users', storagePrefix: 'users' });
 */
import {
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
  signInWithCustomToken,
  signOut,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

const DEFAULT_SSO_EXCHANGE_URL = 'https://europe-west2-group-marketing-hub.cloudfunctions.net/exchangeSSOToken';
const DEFAULT_TROVEX_ID_URL = 'https://id.trovex.com';
const DEFAULT_ALLOWED_DOMAINS = ['@trovex.com', '@focuswashrooms.com'];
const ACCESS_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

function safeGet(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeSet(key, value) {
  try { localStorage.setItem(key, value); } catch { /* storage full or disabled */ }
}
function safeRemove(key) {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

/**
 * Title-case the email local-part as a displayName fallback when Firebase
 * Auth doesn't carry one (common for SSO custom-token sign-ins):
 * "arne.cowley" -> "Arne Cowley".
 */
export function deriveDisplayName(email) {
  if (!email) return '';
  const local = email.split('@')[0] || '';
  return local
    .split(/[._-]/)
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

export function createAuthCore(config) {
  const {
    auth,
    db,
    functions,
    appId,
    platformId,
    storagePrefix,
    allowedDomains = DEFAULT_ALLOWED_DOMAINS,
    trovexIdUrl = DEFAULT_TROVEX_ID_URL,
    ssoExchangeUrl = DEFAULT_SSO_EXCHANGE_URL,
    requestCodeFnName = 'authRequestEmailCode',
    verifyCodeFnName = 'authVerifyEmailCode',
    accessCacheTtlMs = ACCESS_CACHE_TTL_MS,
    // Optional telemetry hook: onEvent(stage, detail) — never throws into
    // auth flow. Apps wire Sentry/analytics here (voip's signin_failure
    // tagging pattern) without the core depending on either.
    onEvent,
    // Optional returnUrl() builder: where Trovex ID sends the person back
    // to after sign-in. Default (none) is window.location.origin. Its
    // result is only used when it is on this app's own origin; see
    // resolveReturnUrl below.
    returnUrl,
    // Optional stripParams(href): the clean URL written back after the SSO
    // params are consumed. Held to the same origin rule.
    stripParams,
  } = config;

  if (!auth || !db || !functions) throw new Error('createAuthCore: auth, db and functions instances are required');
  if (!appId || !platformId || !storagePrefix) throw new Error('createAuthCore: appId, platformId and storagePrefix are required');

  const KEYS = {
    persistence: `${storagePrefix}:authPersistence`,
    loggedOut: `${storagePrefix}:ssoLoggedOut`,
    hadSession: `${storagePrefix}:hadSession`,
    emailCache: `${storagePrefix}:emailByUid:v1`,
    accessCache: (email) => `${storagePrefix}:accessCheck:${email.toLowerCase()}`,
  };

  const emit = (stage, detail) => {
    try { onEvent?.(stage, detail); } catch { /* telemetry must never break auth */ }
  };

  // ── Same-origin return check ─────────────────────────────────────────
  // id.trovex.com accepts a return to ANY host on its allowlist (about 50
  // Trovex hosts plus localhost, apps/id-portal/modules/ssoReturnUrl.js), so
  // a consumer's builder bug could bounce a signed-in person to another app
  // or to localhost with a fresh SSO token. Only the core can pin the return
  // to its own origin, so it refuses anything else and falls back to the
  // origin. Exact comparison: scheme, host and port must all match
  // window.location.origin (no subdomains, no http/https swap, no port
  // change). App-level guards (the wiki's src/lib/returnUrl.js) stay too, as
  // defence in depth. The refused URL is never logged or emitted: it can
  // carry a token.
  function sameOriginHref(raw, origin) {
    if (typeof raw !== 'string' || raw === '') return { reason: 'not-string' };
    let u;
    try { u = new URL(raw, origin); } catch { return { reason: 'unparseable' }; }
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return { reason: 'scheme' };
    if (u.origin !== origin) return { reason: 'off-origin' };
    if (u.username || u.password) return { reason: 'credentials' };
    if (u.pathname.startsWith('//')) return { reason: 'double-slash-path' };
    return { href: u.href };
  }

  function resolveReturnUrl() {
    const origin = window.location.origin;
    if (typeof returnUrl !== 'function') return origin;
    let raw;
    try { raw = returnUrl(); } catch {
      emit('return-url-refused', { reason: 'threw' });
      return origin;
    }
    const checked = sameOriginHref(raw, origin);
    if (!checked.href) {
      emit('return-url-refused', { reason: checked.reason });
      return origin;
    }
    return checked.href;
  }

  // The URL written back after the SSO params are consumed. Default is
  // today's origin + pathname; a stripParams result is used only when it
  // passes the same origin rule, and the core deletes the SSO params from it
  // itself, so a stripParams that keeps them cannot leave the spent token in
  // the address bar or history.
  const SSO_PARAMS = ['sso_token', 'sso_attempted'];
  function cleanedHref() {
    const fallback = window.location.origin + window.location.pathname;
    if (typeof stripParams !== 'function') return fallback;
    let raw;
    try { raw = stripParams(window.location.href); } catch { return fallback; }
    const href = sameOriginHref(raw, window.location.origin).href;
    if (!href) return fallback;
    const u = new URL(href);
    for (const p of SSO_PARAMS) u.searchParams.delete(p);
    return u.href;
  }

  // ── Persistence ──────────────────────────────────────────────────────
  // Default = browserLocalPersistence so auth survives tab close. Rollback
  // hatch: localStorage.setItem('<prefix>:authPersistence', 'session') for
  // users with corrupted IndexedDB — no deploy needed.
  function resolvePersistence() {
    if (safeGet(KEYS.persistence) === 'session') {
      console.warn(`[${storagePrefix}] Auth fallback: using browserSessionPersistence (via ${KEYS.persistence} flag)`);
      return browserSessionPersistence;
    }
    return browserLocalPersistence;
  }

  async function applyPersistence() {
    try {
      await setPersistence(auth, resolvePersistence());
    } catch (e) {
      console.warn(`[${storagePrefix}] setPersistence failed:`, e?.message || e);
    }
  }

  // ── Per-uid email cache ──────────────────────────────────────────────
  // Once a Trovex email has been resolved for a uid (from user.email or
  // the trovexEmail claim), cache it so a weak-network reload can resolve
  // the user without any network at all.
  function readEmailCache() {
    try { return JSON.parse(safeGet(KEYS.emailCache) || '{}') || {}; } catch { return {}; }
  }
  function cacheEmail(uid, email) {
    if (!uid || !email) return;
    const cache = readEmailCache();
    cache[uid] = email;
    // Cap at 5 uids to stay well under storage quotas.
    const keys = Object.keys(cache);
    if (keys.length > 5) {
      for (const k of keys.slice(0, keys.length - 5)) delete cache[k];
    }
    safeSet(KEYS.emailCache, JSON.stringify(cache));
  }
  function lookupCachedEmail(uid) {
    if (!uid) return '';
    return readEmailCache()[uid] || '';
  }

  /**
   * Resolve the Trovex email for a Firebase user. SSO custom-token users
   * carry it in the trovexEmail claim rather than user.email; reading the
   * token can fail on a network blip, so retry with backoff then fall
   * back to the per-uid cache.
   * @returns {{ email: string, fromCache: boolean }}
   */
  // Error codes that mean the restored session is DEAD (revoked / deleted /
  // disabled user - every token refresh 400s), not a network blip. Falling back
  // to the cached email here would render a "zombie" session that looks signed
  // in while every Firestore read is permission-denied; the only sane move is a
  // clean sign-out so the real Login screen shows.
  const TERMINAL_AUTH_CODES = new Set([
    'auth/user-token-expired', 'auth/user-disabled', 'auth/user-not-found',
    'auth/invalid-user-token', 'auth/invalid-refresh-token',
  ]);

  async function resolveUserEmail(firebaseUser) {
    if (!firebaseUser) return { email: '', fromCache: false };
    if (firebaseUser.email) return { email: firebaseUser.email, fromCache: false };
    let email = '';
    for (let attempt = 0; attempt < 4 && !email; attempt += 1) {
      try {
        const tokenResult = await firebaseUser.getIdTokenResult(attempt > 0);
        email = tokenResult?.claims?.trovexEmail || '';
      } catch (err) {
        if (TERMINAL_AUTH_CODES.has(err?.code)) {
          console.warn(`[${storagePrefix}] auth: persisted session is dead (${err.code}) - signing out the stale session`);
          try { await signOut(auth); } catch { /* ignore */ }
          return { email: '', fromCache: false };
        }
        if (attempt < 3) await new Promise((r) => setTimeout(r, 400 * 2 ** attempt));
      }
    }
    if (email) return { email, fromCache: false };
    const cached = lookupCachedEmail(firebaseUser.uid);
    if (cached) {
      console.warn(`[${storagePrefix}] auth: token read failed, using cached email for uid`, firebaseUser.uid);
      return { email: cached, fromCache: true };
    }
    return { email: '', fromCache: false };
  }

  function isAllowedEmail(email) {
    const lower = String(email || '').toLowerCase();
    return allowedDomains.some((d) => lower.endsWith(d));
  }

  // ── Session flags ────────────────────────────────────────────────────
  function hasPriorSession() {
    return safeGet(KEYS.hadSession) === '1';
  }
  function markSessionEstablished(uid, email, { fromCache = false } = {}) {
    safeSet(KEYS.hadSession, '1');
    safeRemove(KEYS.loggedOut);
    if (!fromCache) cacheEmail(uid, email);
  }
  function clearSessionFlags() {
    safeRemove(KEYS.hadSession);
    safeRemove(KEYS.emailCache);
  }

  // ── SSO token exchange ───────────────────────────────────────────────
  /**
   * If the current URL carries an sso_token from id.trovex.com, exchange
   * it for a custom token and sign in. Cleans the URL either way.
   * Call BEFORE starting the auth-state listener.
   */
  async function consumeSsoToken() {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('sso_token');
    if (!ssoToken) return false;

    let signedIn = false;
    if (safeGet(KEYS.loggedOut)) {
      safeRemove(KEYS.loggedOut);
    } else {
      try {
        emit('sso:exchange-start');
        const res = await fetch(ssoExchangeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: ssoToken }),
        });
        const data = await res.json();
        emit('sso:exchange-done', { ok: res.ok });
        if (data.customToken) {
          const cred = await signInWithCustomToken(auth, data.customToken);
          // The custom token already carries the claims; force one refresh
          // so the local SDK state picks up trovexEmail immediately.
          if (cred.user) await cred.user.getIdToken(true);
          signedIn = true;
        }
      } catch (err) {
        console.error(`[${storagePrefix}] SSO token exchange error:`, err);
        emit('sso-exchange-failed', { error: err });
      }
    }
    window.history.replaceState(null, '', cleanedHref());
    return signedIn;
  }

  /** True when SSO was attempted but produced no user (loop breaker). */
  function consumeSsoAttemptedFlag() {
    if (typeof window === 'undefined') return false;
    const p = new URLSearchParams(window.location.search);
    if (!p.get('sso_attempted')) return false;
    window.history.replaceState(null, '', cleanedHref());
    return true;
  }

  // ── Platform access (soft gate — server callables are the boundary) ──
  // Cached per email, stale-while-revalidate: a fresh cache hit returns
  // instantly and refreshes in the background. Fails open — the real
  // security boundary is server-side.
  function readAccessCache(email) {
    try {
      const raw = safeGet(KEYS.accessCache(email));
      if (!raw) return null;
      const { result, ts } = JSON.parse(raw);
      if (typeof result !== 'boolean' || typeof ts !== 'number') return null;
      return { result, fresh: Date.now() - ts < accessCacheTtlMs };
    } catch { return null; }
  }
  function writeAccessCache(email, result) {
    safeSet(KEYS.accessCache(email), JSON.stringify({ result, ts: Date.now() }));
  }
  // UX gate, not a security boundary: allows on a missing doc and on a read
  // error. Rules and server checks are the boundary. See modules/auth/README.md.
  async function fetchPlatformAccess(email) {
    try {
      const snap = await getDoc(doc(db, 'platformAccess', platformId));
      if (!snap.exists()) return true; // no config = unrestricted (matches server)
      const allowed = (snap.data().allowedEmails || []).map((e) => e.toLowerCase());
      return allowed.includes(email.toLowerCase());
    } catch (err) {
      console.warn(`[${storagePrefix}] Failed to check platform access:`, err?.message || err);
      return true;
    }
  }
  async function checkPlatformAccess(email) {
    const cached = readAccessCache(email);
    if (cached?.fresh) {
      fetchPlatformAccess(email).then((r) => writeAccessCache(email, r)).catch(() => {});
      return cached.result;
    }
    const result = await fetchPlatformAccess(email);
    writeAccessCache(email, result);
    return result;
  }

  // ── 6-digit email-code sign-in (generic callables) ───────────────────
  async function requestEmailCode(rawEmail) {
    const email = String(rawEmail || '').trim().toLowerCase();
    if (!isAllowedEmail(email)) {
      throw new Error('Enter your @trovex.com email address.');
    }
    const fn = httpsCallable(functions, requestCodeFnName, { timeout: 20000 });
    try {
      await fn({ appId, email });
    } catch (err) {
      emit('request-code-failed', { error: err });
      throw err;
    }
    return { sent: true, email };
  }

  async function verifyEmailCode(rawEmail, rawCode) {
    const email = String(rawEmail || '').trim().toLowerCase();
    const code = String(rawCode || '').trim();
    const fn = httpsCallable(functions, verifyCodeFnName, { timeout: 20000 });
    let res;
    try {
      res = await fn({ appId, email, code });
    } catch (err) {
      emit('verify-code-failed', { error: err });
      throw err;
    }
    const customToken = res?.data?.customToken;
    if (!customToken) {
      const err = new Error('Sign-in failed. Please try again.');
      emit('verify-code-no-token', { error: err });
      throw err;
    }
    let cred;
    try {
      cred = await signInWithCustomToken(auth, customToken);
    } catch (err) {
      emit('custom-token-signin-failed', { error: err });
      throw err;
    }
    // Provision the central Trovex ID (fire-and-forget; non-fatal if slow).
    if (cred?.user) {
      try {
        const linkFn = httpsCallable(functions, 'linkUserToTrovexId', { timeout: 15000 });
        linkFn({ email }).catch((e) => console.warn(`[${storagePrefix}] linkUserToTrovexId failed (non-fatal):`, e?.message || e));
      } catch { /* ignore */ }
      // Pick up the trovexEmail claim so the access gate sees it immediately.
      try { await cred.user.getIdToken(true); } catch { /* ignore */ }
    }
    return { success: true };
  }

  // ── Trovex ID redirects ──────────────────────────────────────────────
  function loginWithTrovexId() {
    safeRemove(KEYS.loggedOut);
    const finalReturn = encodeURIComponent(resolveReturnUrl());
    const loginUrl = `${trovexIdUrl}/?returnUrl=${finalReturn}&action=login`;
    // Bounce through logout first so a stale Trovex ID session can't
    // instantly redirect back without showing the sign-in form.
    window.location.href = `${trovexIdUrl}/?action=logout&returnUrl=${encodeURIComponent(loginUrl)}`;
  }

  async function logout() {
    try { await signOut(auth); } catch (err) { console.warn(`[${storagePrefix}] signOut failed:`, err?.message || err); }
    clearSessionFlags();
    safeSet(KEYS.loggedOut, '1');
    // Round-trip Trovex ID's logout endpoint so its own session is
    // cleared too — otherwise the next visit auto-signs straight back in.
    const returnUrl = encodeURIComponent(window.location.origin + '/');
    window.location.href = `${trovexIdUrl}/?action=logout&returnUrl=${returnUrl}`;
  }

  return {
    appId,
    platformId,
    storagePrefix,
    auth,
    keys: KEYS,
    applyPersistence,
    consumeSsoToken,
    consumeSsoAttemptedFlag,
    resolveUserEmail,
    isAllowedEmail,
    hasPriorSession,
    markSessionEstablished,
    clearSessionFlags,
    checkPlatformAccess,
    requestEmailCode,
    verifyEmailCode,
    loginWithTrovexId,
    resolveReturnUrl,
    logout,
  };
}
